(function () {
  if (customElements.get('mobile-product-purchase-bar')) return;

  customElements.define(
    'mobile-product-purchase-bar',
    class MobileProductPurchaseBar extends HTMLElement {
      constructor() {
        super();
        this.homeLink = this.querySelector('[data-mobile-product-home]');
        this.cartLink = this.querySelector('[data-mobile-product-cart]');
        this.addButton = this.querySelector('[data-mobile-product-add]');
        this.addButtonLabel = this.querySelector('[data-mobile-product-add-label]');
        this.buyButton = this.querySelector('[data-mobile-product-buy]');
        this.status = this.querySelector('[data-mobile-product-status]');
        this.targetAddButton = null;
        this.targetBuyButton = null;
        this.targetCartButton = null;
        this.targetObserver = null;
        this.contentObserver = null;
        this.hiddenSourceElements = [];
        this.variantUnsubscriber = null;
        this.cartErrorUnsubscriber = null;
        this.syncFrame = null;
      }

      connectedCallback() {
        if (!this.homeLink || !this.cartLink || !this.addButton || !this.buyButton) return;

        this.handleHomeClick = this.handleHomeClick.bind(this);
        this.handleCartClick = this.handleCartClick.bind(this);
        this.handleAddClick = this.handleAddClick.bind(this);
        this.handleBuyClick = this.handleBuyClick.bind(this);
        this.scheduleSync = this.scheduleSync.bind(this);

        this.homeLink.addEventListener('click', this.handleHomeClick);
        this.cartLink.addEventListener('click', this.handleCartClick);
        this.addButton.addEventListener('click', this.handleAddClick);
        this.buyButton.addEventListener('click', this.handleBuyClick);
        document.addEventListener('change', this.scheduleSync, true);
        window.addEventListener('resize', this.scheduleSync);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.scheduleSync);
          this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
            const message = event && event.data ? event.data.message || event.data.errors : '';
            if (this.status && message) this.status.textContent = String(message);
          });
        }

        const mainContent = document.getElementById('MainContent');
        if (mainContent && typeof MutationObserver === 'function') {
          this.contentObserver = new MutationObserver(this.scheduleSync);
          this.contentObserver.observe(mainContent, { childList: true, subtree: true });
        }

        this.syncTargets();
      }

      disconnectedCallback() {
        this.homeLink?.removeEventListener('click', this.handleHomeClick);
        this.cartLink?.removeEventListener('click', this.handleCartClick);
        this.addButton?.removeEventListener('click', this.handleAddClick);
        this.buyButton?.removeEventListener('click', this.handleBuyClick);
        document.removeEventListener('change', this.scheduleSync, true);
        window.removeEventListener('resize', this.scheduleSync);
        this.targetObserver?.disconnect();
        this.contentObserver?.disconnect();
        this.variantUnsubscriber?.();
        this.cartErrorUnsubscriber?.();
        if (this.syncFrame) window.cancelAnimationFrame(this.syncFrame);
        this.revealSourceActions();
        document.body.classList.remove('mobile-product-purchase-bar-ready');
      }

      findTargetAddButton() {
        return (
          document.querySelector('.torras-main-product [data-add-to-cart]') ||
          document.querySelector("product-info[id^='MainProduct'] .product-form__submit") ||
          Array.from(document.querySelectorAll("#MainContent product-form form[data-type='add-to-cart-form'] button[name='add']")).find(
            (button) => !button.closest('mobile-product-purchase-bar')
          ) ||
          null
        );
      }

      findTargetBuyButton() {
        return document.querySelector('.torras-main-product [data-buy-now]');
      }

      findTargetCartButton() {
        return (
          document.querySelector('.clone-header__mobile [data-cart-drawer-trigger]') ||
          document.querySelector('[data-cart-drawer-trigger]')
        );
      }

      scheduleSync() {
        if (this.syncFrame) return;
        this.syncFrame = window.requestAnimationFrame(() => {
          this.syncFrame = null;
          this.syncTargets();
        });
      }

      revealSourceActions() {
        this.hiddenSourceElements.forEach((element) => element.removeAttribute('data-mobile-product-source-hidden'));
        this.hiddenSourceElements = [];
      }

      hideSourceActions() {
        this.revealSourceActions();
        if (!window.matchMedia('(max-width: 749px)').matches || !this.targetAddButton) return;

        const actionContainer = this.targetAddButton.closest('.torras-main-product__actions');
        if (actionContainer && (!this.targetBuyButton || actionContainer.contains(this.targetBuyButton))) {
          this.hiddenSourceElements = [actionContainer];
        } else {
          const addContainer = this.targetAddButton.closest('product-form') || this.targetAddButton;
          this.hiddenSourceElements = [addContainer, this.targetBuyButton].filter(Boolean);
        }

        this.hiddenSourceElements.forEach((element) => element.setAttribute('data-mobile-product-source-hidden', ''));
      }

      syncTargets() {
        const nextAddButton = this.findTargetAddButton();
        const nextBuyButton = this.findTargetBuyButton();

        if (nextAddButton !== this.targetAddButton || nextBuyButton !== this.targetBuyButton) {
          this.targetObserver?.disconnect();
          this.revealSourceActions();
          this.targetAddButton = nextAddButton;
          this.targetBuyButton = nextBuyButton;

          if (this.targetAddButton && typeof MutationObserver === 'function') {
            this.targetObserver = new MutationObserver(() => this.syncState());
            this.targetObserver.observe(this.targetAddButton, {
              attributes: true,
              attributeFilter: ['class', 'disabled', 'aria-disabled'],
              childList: true,
              subtree: true,
            });
            if (this.targetBuyButton) {
              this.targetObserver.observe(this.targetBuyButton, {
                attributes: true,
                attributeFilter: ['class', 'disabled', 'aria-disabled'],
              });
            }
          }
        }

        if (!this.targetAddButton) {
          this.hidden = true;
          this.revealSourceActions();
          document.body.classList.remove('mobile-product-purchase-bar-ready');
          return;
        }

        this.hidden = false;
        document.body.classList.add('mobile-product-purchase-bar-ready');
        this.hideSourceActions();
        this.syncState();
      }

      syncState() {
        if (!this.targetAddButton) return;

        const addDisabled =
          this.targetAddButton.disabled || this.targetAddButton.getAttribute('aria-disabled') === 'true';
        const buyDisabled = this.targetBuyButton
          ? this.targetBuyButton.disabled || this.targetBuyButton.getAttribute('aria-disabled') === 'true'
          : addDisabled;
        const isLoading = this.targetAddButton.classList.contains('loading');
        const sourceLabel = this.targetAddButton.querySelector('span:not(.loading__spinner)');
        const sourceLabelText = sourceLabel ? sourceLabel.textContent.trim() : '';

        this.addButton.disabled = addDisabled;
        this.buyButton.disabled = buyDisabled;
        this.addButton.classList.toggle('loading', isLoading);

        const spinner = this.addButton.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !isLoading);

        if (this.addButtonLabel) {
          this.addButtonLabel.textContent = sourceLabelText || this.dataset.fallbackAddLabel || 'Add to Cart';
        }
      }

      handleHomeClick(event) {
        event.preventDefault();
        window.location.assign(this.homeLink.href);
      }

      handleCartClick(event) {
        event.preventDefault();
        this.targetCartButton = this.findTargetCartButton();

        if (this.targetCartButton) {
          this.proxyClick(this.targetCartButton);
          return;
        }

        window.location.assign(this.cartLink.href);
      }

      handleAddClick() {
        this.syncTargets();
        if (!this.targetAddButton || this.addButton.disabled) return;
        if (this.status) this.status.textContent = '';
        this.proxyClick(this.targetAddButton);
        this.scheduleSync();
      }

      handleBuyClick() {
        this.syncTargets();
        if (!this.targetAddButton || this.buyButton.disabled) return;
        if (this.status) this.status.textContent = '';

        if (this.targetBuyButton) {
          this.proxyClick(this.targetBuyButton);
          return;
        }

        this.directCheckout();
      }

      proxyClick(target) {
        const tracksClick = target.hasAttribute('data-analytics-click');
        const analyticsClickValue = target.getAttribute('data-analytics-click');

        if (tracksClick) target.removeAttribute('data-analytics-click');
        try {
          target.click();
        } finally {
          if (tracksClick) target.setAttribute('data-analytics-click', analyticsClickValue || '');
        }
      }

      getPurchaseData() {
        const productRoot = this.targetAddButton.closest('.torras-main-product, product-info, .shopify-section');
        const form = this.targetAddButton.closest('form');
        const variantInput =
          (form && form.querySelector("input[name='id']")) ||
          (productRoot && productRoot.querySelector("form[data-type='add-to-cart-form'] input[name='id']"));
        const quantityInput =
          (productRoot && productRoot.querySelector('[data-form-quantity]')) ||
          (productRoot && productRoot.querySelector("input[name='quantity']")) ||
          (productRoot && productRoot.querySelector('[data-quantity-input]'));

        return {
          id: variantInput ? variantInput.value : '',
          quantity: Math.max(1, parseInt(quantityInput ? quantityInput.value : '1', 10) || 1),
        };
      }

      directCheckout() {
        const purchase = this.getPurchaseData();
        if (!purchase.id) return;

        const formData = new FormData();
        formData.append('id', purchase.id);
        formData.append('quantity', String(purchase.quantity));
        this.buyButton.disabled = true;
        this.buyButton.setAttribute('aria-busy', 'true');

        fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        })
          .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
          .then(async ({ ok, data }) => {
            if (!ok || data.status) throw new Error(data.description || data.message || 'Unable to start checkout.');
            const allowed = window.TorrasLaunch && await window.TorrasLaunch.beforeCheckout();
            window.location.assign(`${window.Shopify?.routes?.root || '/'}${allowed ? 'checkout' : 'cart'}`);
          })
          .catch((error) => {
            if (this.status) this.status.textContent = error.message;
            this.buyButton.disabled = false;
            this.buyButton.removeAttribute('aria-busy');
          });
      }
    }
  );
})();
